import React, { useEffect, useMemo, useState } from "react";
import { Building2, Check, Loader2, Phone } from "lucide-react";

const isSetupHost = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const host = String(window.location?.hostname || "").toLowerCase();
  const electron = String(navigator.userAgent || "").toLowerCase().includes("electron");
  return electron || host === "localhost" || host === "127.0.0.1";
};

const SETUP_STATUS_RETRY_DELAYS_MS = [250, 500, 1000, 2000, 5000];

const waitFor = (delayMs) =>
  new Promise((resolve) => window.setTimeout(resolve, delayMs));

export const loadInstallationSetupStatus = async ({
  fetchImpl = fetch,
  wait = waitFor,
  isCancelled = () => false,
} = {}) => {
  let attempt = 0;
  while (!isCancelled()) {
    try {
      const response = await fetchImpl("/api/installation/setup-status", {
        credentials: "same-origin",
        cache: "no-store",
      });
      // Compatibilité avec un ancien serveur qui ne connaît pas encore la route.
      if (response.status === 404) return { required: false };
      if (!response.ok) throw new Error("setup-status-unavailable");
      return await response.json();
    } catch (error) {
      if (isCancelled()) return null;
      const delay =
        SETUP_STATUS_RETRY_DELAYS_MS[
          Math.min(attempt, SETUP_STATUS_RETRY_DELAYS_MS.length - 1)
        ];
      attempt += 1;
      await wait(delay);
    }
  }
  return null;
};

const FirstRunSetup = () => {
  const setupHost = useMemo(isSetupHost, []);
  const [status, setStatus] = useState("checking");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!setupHost) {
      setStatus("not-required");
      return undefined;
    }

    let cancelled = false;
    const loadStatus = async () => {
      const payload = await loadInstallationSetupStatus({
        isCancelled: () => cancelled,
      });
      if (cancelled || !payload) return;
      if (!payload?.required) {
        setStatus("not-required");
        return;
      }
      setName(String(payload?.name || ""));
      setContact(String(payload?.contact || ""));
      setStatus("required");
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [setupHost]);

  const submit = async (event) => {
    event.preventDefault();
    const normalizedName = name.trim();
    const normalizedContact = contact.trim();
    if (normalizedName.length < 2) {
      setError("Saisissez le nom de l’établissement.");
      return;
    }
    if (normalizedContact.length < 5) {
      setError("Saisissez un contact valide.");
      return;
    }

    setError("");
    setStatus("saving");
    try {
      const response = await fetch("/api/installation/setup", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: normalizedName, contact: normalizedContact }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Impossible d’enregistrer la configuration.");
      }
      try {
        localStorage.setItem("restaurantInfo", JSON.stringify(payload.restaurantInfo || {}));
      } catch (_error) {}
      setStatus("saved");
      window.setTimeout(() => window.location.reload(), 650);
    } catch (submitError) {
      setError(submitError?.message || "Impossible d’enregistrer la configuration.");
      setStatus("required");
    }
  };

  if (!setupHost || !["required", "saving", "saved"].includes(status)) return null;

  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center overflow-y-auto bg-[#050b18] px-5 py-8 text-white">
      <div className="w-full max-w-xl rounded-[2rem] border border-white/10 bg-[#0b1529] p-6 shadow-2xl sm:p-9">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-400 text-[#07101f]">
            <img src="/moo-sync-insigne-logo.png" alt="" className="h-9 w-9 object-contain" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-amber-300">
              Première installation
            </p>
            <h1 className="mt-1 text-2xl font-bold sm:text-3xl">Bienvenue sur MOO Sync</h1>
          </div>
        </div>

        {status === "saved" ? (
          <div className="mt-9 flex flex-col items-center rounded-3xl border border-emerald-400/25 bg-emerald-400/10 px-6 py-10 text-center">
            <Check className="h-12 w-12 text-emerald-300" />
            <p className="mt-4 text-xl font-semibold">Votre établissement est prêt.</p>
            <p className="mt-2 text-sm text-white/60">Ouverture de MOO Sync…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <p className="text-sm leading-6 text-white/65">
              Ces informations apparaîtront dans les paramètres et sur vos documents de caisse.
            </p>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white/80">
                Nom de l’établissement
              </span>
              <span className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.04] px-4 focus-within:border-amber-300/70">
                <Building2 className="h-5 w-5 shrink-0 text-amber-300" />
                <input
                  autoFocus
                  required
                  maxLength={120}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ex. Restaurant La Terrasse"
                  className="min-w-0 flex-1 bg-transparent py-4 text-base text-white outline-none placeholder:text-white/30"
                />
              </span>
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-white/80">Contact</span>
              <span className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.04] px-4 focus-within:border-amber-300/70">
                <Phone className="h-5 w-5 shrink-0 text-amber-300" />
                <input
                  required
                  maxLength={60}
                  value={contact}
                  onChange={(event) => setContact(event.target.value)}
                  placeholder="Ex. +225 07 00 00 00 00"
                  className="min-w-0 flex-1 bg-transparent py-4 text-base text-white outline-none placeholder:text-white/30"
                />
              </span>
            </label>
            {error ? (
              <p role="alert" className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={status === "saving"}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-400 px-5 py-4 font-bold text-[#07101f] transition hover:bg-amber-300 disabled:cursor-wait disabled:opacity-70"
            >
              {status === "saving" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
              {status === "saving" ? "Configuration…" : "Démarrer MOO Sync"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default FirstRunSetup;
